import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import { isSessionEnded, sessionMark } from "stores/sessionGuard";
import { kindDef, vocabulary, type TrackerFieldDef, type TrackerImportKind } from "shared/trackers/vocabulary";
import { parseTrackerFile, TrackerFileError, type Cell, type ParsedSheet } from "./parseTrackerFile";

/** Blake's homegrown trackers (#3075 / #2663) — the one source system so far. */
export const BLAKE_TRACKER = "blake-tracker";
/** The 1 MB JSON body, with room for the envelope. */
export const MAX_BODY_CHARS = 950_000;

export type Outcome = "create" | "update" | "unchanged" | "skip" | "reject";
export const OUTCOMES: readonly Outcome[] = ["create", "update", "unchanged", "skip", "reject"];
export const OUTCOME_LABELS: Record<Outcome, string> = {
  create: "New",
  update: "Changes",
  unchanged: "Unchanged",
  skip: "Skipped",
  reject: "Rejected",
};

export interface ImportMapping {
  columns: Record<string, string>;
  values?: Record<string, Record<string, string>>;
}

export interface RowReport {
  row: number;
  sourceId: string | null;
  outcome: Outcome;
  reasons: string[];
  warnings: string[];
  clientId: string | null;
  clientName: string | null;
  summary: string;
  changes: string[];
}

/** The API's answer to a dry run or an import (tracker-imports.ts `TrackerImportResult`). */
export interface ImportReport {
  kind: TrackerImportKind;
  sourceSystem: string;
  sourceSystemLabel: string;
  commit: boolean;
  batchId: string | null;
  nothingToWrite: boolean;
  mapping: ImportMapping;
  mappingSuggested: boolean;
  missingRequired: string[];
  unknownColumns: string[];
  counts: Record<Outcome, number>;
  unmatched: Record<string, string[]>;
  rows: RowReport[];
}

export interface ImportBatch {
  id: string;
  kind: string;
  sourceSystemLabel: string;
  fileName: string | null;
  rowsSha256: string | null;
  mapping: ImportMapping | null;
  counts: Record<Outcome, number>;
  rowCount: number;
  createdAt: string | null;
  createdByName: string | null;
}

/** A value-mapping choice: what HQ may read a file value as. */
export interface ValueOption {
  value: string;
  label: string;
}

type RawService = { service: (path: string) => { create: (data: object) => Promise<unknown> } };

/**
 * Admin → Import trackers: Blake's AR, contracts and onboarding exports into HQ.
 *
 *   1. pick what the file is (the kind) and the file; the browser reads it;
 *   2. the first DRY RUN asks HQ to suggest which column feeds which field,
 *      and says what every row would do — new, changes, unchanged, skipped
 *      (removed in HQ) or rejected, with the reasons;
 *   3. adjust the columns and map the values HQ could not match (a client's
 *      name, a status word, an owner) — each change runs the dry run again;
 *   4. import: HQ plans again, in one transaction, and writes what it says.
 *
 * The plan is HQ's, never this page's: the import sends the same file and
 * mapping and the API decides again. The dry-run answer is a REPORT about a
 * file, not an API entity, so it is held here; the committed runs (history)
 * live in the `tracker-imports` ServiceStore. Calls go through the RAW Feathers
 * service, so a refusal shows in the API's words.
 */
export default class TrackerImportStore {
  rootStore: RootStore;

  kind: TrackerImportKind = "ar-invoices";
  fileName: string | null = null;
  sheets: ParsedSheet[] = [];
  sheetIndex = 0;
  reading = false;
  fileError: string | null = null;

  mapping: ImportMapping | null = null;
  /** The report describes the current file, kind, sheet and mapping. */
  reportCurrent = false;
  report: ImportReport | null = null;
  checking = false;
  committing = false;
  error: string | null = null;
  /** What the last import did, said once. */
  committedMessage: string | null = null;
  outcomeFilter: Outcome | "all" = "all";

  historyError: string | null = null;

  private seq = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private store(path: string): ServiceStoreType | null {
    return (this.rootStore.serviceStoreManager?.getStore(path) as unknown as ServiceStoreType | null) ?? null;
  }

  get fields(): TrackerFieldDef[] {
    return kindDef(this.kind)?.fields ?? [];
  }

  get sheet(): ParsedSheet | null {
    return this.sheets[this.sheetIndex] ?? null;
  }

  get headers(): string[] {
    return this.sheet?.headers ?? [];
  }

  /** The committed runs, newest first. */
  get history(): ImportBatch[] {
    return ((this.store("tracker-imports")?.entities ?? []) as unknown as ImportBatch[])
      .filter((b) => b?.id)
      .slice()
      .sort((a, b) => String(b.createdAt ?? "").localeCompare(String(a.createdAt ?? "")));
  }

  /** The last committed run of this kind, whose mapping a re-run can reuse. */
  get lastBatchOfKind(): ImportBatch | null {
    return this.history.find((b) => b.kind === this.kind && b.mapping) ?? null;
  }

  get rows(): RowReport[] {
    const rows = this.report?.rows ?? [];
    return this.outcomeFilter === "all" ? rows : rows.filter((r) => r.outcome === this.outcomeFilter);
  }

  /** Rows the import would write. */
  get writable(): number {
    return (this.report?.counts.create ?? 0) + (this.report?.counts.update ?? 0);
  }

  get canCommit(): boolean {
    return this.reportCurrent && !this.report?.commit && this.writable > 0 && !this.checking && !this.committing;
  }

  /** The column a field is mapped to, or "" (not in this file). */
  columnFor(field: string): string {
    return this.mapping?.columns[field] ?? "";
  }

  /** What a value HQ could not match is mapped to, or "". */
  valueFor(field: string, raw: string): string {
    return this.mapping?.values?.[field]?.[raw] ?? "";
  }

  /** The choices for mapping a file value, by field type. */
  valueOptions(field: TrackerFieldDef): ValueOption[] {
    if (field.type === "enum") return vocabulary(field.enum ?? "").map((v) => ({ value: v.key, label: v.label }));
    if (field.type === "client") {
      const rows = (this.store("clients")?.entities ?? []) as unknown as Array<{ id: string; name?: string; code?: string; deletedAt?: string | null }>;
      return rows
        .filter((c) => !c.deletedAt)
        .map((c) => ({ value: String(c.id), label: c.code ? `${c.name ?? "—"} (${c.code})` : (c.name ?? "—") }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }
    if (field.type === "person") {
      const rows = (this.store("user")?.entities ?? []) as unknown as Array<{
        id: number | string;
        firstName?: string | null;
        lastName?: string | null;
        email?: string | null;
        isValidated?: boolean | number | null;
      }>;
      return [
        { value: "", label: "Nobody (import with no owner)" },
        ...rows
          .filter((u) => u.isValidated !== false && u.isValidated !== 0)
          .map((u) => ({ value: String(u.id), label: [u.firstName, u.lastName].filter(Boolean).join(" ").trim() || u.email || `User ${u.id}` }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      ];
    }
    return [];
  }

  /** The fields the dry run listed unmatched values for, with those values. */
  get unmatchedFields(): Array<{ field: TrackerFieldDef; values: string[] }> {
    const unmatched = this.report?.unmatched ?? {};
    return this.fields.filter((f) => (unmatched[f.key] ?? []).length > 0).map((f) => ({ field: f, values: unmatched[f.key] }));
  }

  setKind(kind: TrackerImportKind) {
    if (kind === this.kind || this.committing) return;
    this.kind = kind;
    this.mapping = null;
    this.invalidate();
  }

  selectSheet(index: number) {
    if (this.committing) return;
    this.sheetIndex = index;
    this.mapping = null;
    this.invalidate();
  }

  setOutcomeFilter(filter: Outcome | "all") {
    this.outcomeFilter = filter;
  }

  setColumn(field: string, heading: string) {
    if (this.committing) return;
    const columns = { ...(this.mapping?.columns ?? {}) };
    if (heading) columns[field] = heading;
    else delete columns[field];
    this.mapping = { ...(this.mapping ?? { columns: {} }), columns };
    this.invalidate();
  }

  setValue(field: string, raw: string, mapped: string) {
    if (this.committing) return;
    const values = { ...(this.mapping?.values ?? {}) };
    const forField = { ...(values[field] ?? {}) };
    if (mapped === "" && this.fields.find((f) => f.key === field)?.type !== "person") delete forField[raw];
    else forField[raw] = mapped;
    values[field] = forField;
    this.mapping = { columns: { ...(this.mapping?.columns ?? {}) }, values };
    this.invalidate();
  }

  /** Start from the mapping the last import of this kind used. */
  reuseLastMapping() {
    const batch = this.lastBatchOfKind;
    if (!batch?.mapping || this.committing) return;
    this.mapping = { columns: { ...batch.mapping.columns }, ...(batch.mapping.values ? { values: { ...batch.mapping.values } } : {}) };
    this.invalidate();
  }

  /**
   * The file, sheet, kind or mapping changed: the report shown no longer
   * describes what an import would send. A dry run still out is DROPPED (its
   * answer is about the old input and must never mark the new one checked),
   * so Import waits for a dry run of what is on screen now.
   */
  private invalidate() {
    this.seq += 1;
    this.checking = false;
    this.reportCurrent = false;
    this.committedMessage = null;
    this.error = null;
  }

  /** Read a chosen file in the browser. Resolves false (with `fileError`) when it cannot be read. */
  async loadFile(file: File): Promise<boolean> {
    if (this.committing) return false;
    const ended = sessionMark(this.rootStore);
    this.reading = true;
    this.fileError = null;
    this.report = null;
    this.mapping = null;
    this.invalidate();
    try {
      const sheets = await parseTrackerFile(file);
      // A sign-out while the file was being read: it is not the next person's.
      if (ended()) return false;
      runInAction(() => {
        this.sheets = sheets;
        this.sheetIndex = 0;
        this.fileName = file.name;
      });
      return true;
    } catch (err) {
      if (isSessionEnded(err) || ended()) return false;
      runInAction(() => {
        this.sheets = [];
        this.fileName = null;
        this.fileError = err instanceof TrackerFileError ? err.message : "The file could not be read.";
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.reading = false;
        });
      }
    }
  }

  private body(commit: boolean): {
    kind: string;
    sourceSystem: string;
    fileName: string | null;
    headers: string[];
    rows: Cell[][];
    rowNumbers: number[];
    date1904: boolean;
    mapping: ImportMapping | null;
    commit: boolean;
  } | null {
    const sheet = this.sheet;
    if (!sheet) return null;
    return {
      kind: this.kind,
      sourceSystem: BLAKE_TRACKER,
      fileName: this.fileName,
      headers: sheet.headers,
      rows: sheet.rows,
      rowNumbers: sheet.rowNumbers,
      date1904: sheet.date1904,
      mapping: this.mapping,
      commit,
    };
  }

  private async send(commit: boolean): Promise<ImportReport | null> {
    const body = this.body(commit);
    if (!body) {
      this.error = "Choose a file first.";
      return null;
    }
    if (JSON.stringify(body).length > MAX_BODY_CHARS) {
      this.error = `This sheet is too large to send in one go (${body.rows.length.toLocaleString("en-US")} rows). Split it into smaller files.`;
      return null;
    }
    const api = this.rootStore.app as unknown as RawService | undefined;
    if (!api) {
      this.error = "Not connected to the API — reload and try again.";
      return null;
    }
    return (await api.service("tracker-imports").create(body)) as ImportReport;
  }

  /** The dry run. With no mapping yet, HQ suggests one; it is kept for the person to adjust. */
  async checkFile(): Promise<boolean> {
    const seq = ++this.seq;
    const ended = sessionMark(this.rootStore);
    this.checking = true;
    this.error = null;
    this.committedMessage = null;
    try {
      const report = await this.send(false);
      if (!report || seq !== this.seq || ended()) return false;
      runInAction(() => {
        this.report = report;
        this.mapping = { ...report.mapping };
        this.reportCurrent = true;
      });
      return true;
    } catch (err) {
      if (seq === this.seq && !ended() && !isSessionEnded(err)) {
        runInAction(() => {
          this.error = loadErrorMessage(err, "The dry run could not be run.");
        });
      }
      return false;
    } finally {
      if (seq === this.seq && !ended()) {
        runInAction(() => {
          this.checking = false;
        });
      }
    }
  }

  /** The import: HQ plans again in one transaction and writes what it says. */
  async commit(): Promise<boolean> {
    if (!this.canCommit) return false;
    // Not tied to `seq`: an import that ran is said, whatever happened on the
    // page meanwhile (and nothing on the page changes while it runs).
    const ended = sessionMark(this.rootStore);
    this.committing = true;
    this.error = null;
    try {
      const report = await this.send(true);
      if (!report || ended()) return false;
      runInAction(() => {
        this.report = report;
        this.reportCurrent = true;
        this.committedMessage = report.nothingToWrite
          ? "Nothing was imported: every row is unchanged, skipped or rejected."
          : `Imported: ${report.counts.create} new, ${report.counts.update} changed. ${report.counts.unchanged} unchanged, ${report.counts.skip} skipped, ${report.counts.reject} rejected.`;
      });
      await this.loadHistory();
      return true;
    } catch (err) {
      if (!ended() && !isSessionEnded(err)) {
        runInAction(() => {
          this.error = loadErrorMessage(err, "The import did not run. Nothing was written.");
        });
      }
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.committing = false;
        });
      }
    }
  }

  async loadHistory(): Promise<void> {
    const ended = sessionMark(this.rootStore);
    this.historyError = null;
    try {
      await findOrThrow(this.store("tracker-imports"), { query: { $limit: 50 } });
    } catch (err) {
      if (ended() || isSessionEnded(err)) return;
      runInAction(() => {
        this.historyError = loadErrorMessage(err, "Earlier imports could not be loaded.");
      });
    }
  }

  /** The lookups the value mapping offers: clients and people. Tolerant. */
  async loadLookups(): Promise<void> {
    await Promise.all([
      this.store("clients")?.find({ query: { $limit: 500 } })?.catch?.(() => undefined),
      this.store("user")?.find({ query: { $limit: 500 } })?.catch?.(() => undefined),
    ]);
  }

  async initialize(): Promise<void> {
    await Promise.all([this.loadHistory(), this.loadLookups()]);
  }

  reset() {
    this.seq += 1;
    this.kind = "ar-invoices";
    this.fileName = null;
    this.sheets = [];
    this.sheetIndex = 0;
    this.reading = false;
    this.fileError = null;
    this.mapping = null;
    this.reportCurrent = false;
    this.report = null;
    this.checking = false;
    this.committing = false;
    this.error = null;
    this.committedMessage = null;
    this.outcomeFilter = "all";
    this.historyError = null;
  }
}
